/**
 * useTier Hook - Unified Tier Architecture (2025-11-28)
 *
 * SINGLE SOURCE OF TRUTH for user tier resolution.
 * Used for BOTH visual identity (fonts, colors) AND business logic (quota, limits).
 *
 * Architecture Decision (Multi-Model Consensus: O3, Gemini Pro 3, GPT-5 Pro):
 * - Backend `get_user_entitlements` RPC is the authoritative source
 *   (replaces get_user_tier which was not migrated to PlanetScale)
 * - Returns both canonical tier AND visual_layer
 * - Correctly handles family member tier inheritance
 * - Decoupled from transactional analyze-drama endpoint
 *
 * Usage:
 * ```tsx
 * import { useTier } from '@/hooks/useTier';
 *
 * function MyComponent() {
 *   const { tier, tierLevel, visualLayer, isPremium, isLoading } = useTier();
 *
 *   // Visual styling
 *   const fontFamily = tierLevel === 3 ? 'Miller Display' : 'Inter';
 *
 *   // Business logic (character limits, feature gating)
 *   const characterLimit = tier === 'dramax' ? 5000 : tier === 'drama_plus' ? 2000 : 1000;
 *
 *   return <Text style={{ fontFamily }}>{content}</Text>;
 * }
 * ```
 *
 * Replaces: useSubscriptionTier (deprecated), TierContext (deprecated)
 *
 * Returns:
 * - tier: 'free' | 'drama_plus' | 'dramax' | 'anonymous'
 * - tierLevel: 1 | 2 | 3 (for visual identity)
 * - visualLayer: 'layer1' | 'layer2' | 'layer3'
 * - source: 'individual' | 'family' | 'anonymous'
 * - isPremium: boolean (tier is drama_plus or dramax)
 * - isLayer1/2/3: boolean helpers
 * - familyPlanId: UUID | null
 * - isFamilyAdmin: boolean
 * - dailyLimit: number (quota limit for reference)
 * - isLoading: boolean
 * - error: Error | null
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSearchParams } from 'expo-router';
import { logNetworkError } from '../lib/logNetworkError';
import { fetchJsonValidated, isAuthRequiredError } from '@dramadetector/api-client';
import { TierDto, type Tier, type TierId, type VisualLayer, type TierSource } from '@dramadetector/shared';
import { getEdgeFunctionUrl } from '../lib/api-url';

/**
 * Tier types — canonical source: `@dramadetector/shared/schemas/tier.ts` (TierDto).
 *
 * These re-exports exist for backward compatibility with consumers that imported
 * from `./useTier`. New code should import directly from `@dramadetector/shared`:
 *   - `TierId`       — `'anonymous' | 'free' | 'drama_plus' | 'dramax'`
 *   - `VisualLayer`  — `'layer1' | 'layer2' | 'layer3'`
 *   - `TierSource`   — `'individual' | 'family' | 'anonymous'`
 *   - `Tier`         — full DTO (z.infer<typeof TierDto>)
 */
export type { TierId, VisualLayer, TierSource, Tier };

/** @deprecated Use `TierId` from `@dramadetector/shared` instead. */
export type CanonicalTier = TierId;

/** @deprecated Use `Tier` from `@dramadetector/shared` instead. */
export type TierData = Tier;

export interface UseTierReturn {
  /** Raw canonical TierDto (source of truth). */
  data: Tier;

  // Canonical tier (for business logic)
  tier: TierId;

  // Visual identity helpers
  tierLevel: 1 | 2 | 3;
  visualLayer: VisualLayer;
  isLayer1: boolean;
  isLayer2: boolean;
  isLayer3: boolean;
  isPremium: boolean;

  // Source information
  source: TierSource;
  familyPlanId: string | null;
  isFamilyAdmin: boolean;

  // Quota reference
  dailyLimit: number;

  // Loading state
  isLoading: boolean;
  error: Error | null;

  // Refresh function
  refetch: () => void;
}

// Default tier data for initial/anonymous state
const DEFAULT_TIER_DATA: Tier = {
  tier: 'anonymous',
  visualLayer: 'layer1',
  source: 'anonymous',
  familyPlanId: null,
  isFamilyAdmin: false,
  dailyLimit: 10, // Weekly limit for anonymous
  updatedAt: new Date().toISOString(),
};

// Map tier to tier level for visual identity
function getTierLevel(tier: TierId): 1 | 2 | 3 {
  switch (tier) {
    case 'dramax':
      return 3;
    case 'drama_plus':
      return 2;
    default:
      return 1;
  }
}

/**
 * Client-side tier → visual layer + daily limit mapping.
 * Mirrors subscription_tiers DB table. Used by data-proxy path.
 */
const TIER_LAYER_MAP: Record<TierId, { visualLayer: VisualLayer; dailyLimit: number }> = {
  anonymous: { visualLayer: 'layer1', dailyLimit: 10 },
  free: { visualLayer: 'layer1', dailyLimit: 10 },
  drama_plus: { visualLayer: 'layer2', dailyLimit: 100 },
  dramax: { visualLayer: 'layer3', dailyLimit: 200 },
};

/**
 * Build TierDto for an individual (non-family) user from tier string.
 * Used by the data-proxy path to avoid RPC for individual users.
 */
function makeIndividualTierData(
  tier: TierId,
  dailyLimit?: number,
  updatedAt?: string,
): Tier {
  const defaults = TIER_LAYER_MAP[tier] ?? TIER_LAYER_MAP.free;
  return {
    tier,
    visualLayer: defaults.visualLayer,
    source: 'individual',
    familyPlanId: null,
    isFamilyAdmin: false,
    dailyLimit: dailyLimit ?? defaults.dailyLimit,
    updatedAt: updatedAt ?? new Date().toISOString(),
  };
}

// Query key factory for tier data
export const tierKeys = {
  all: ['tier'] as const,
  // hasJwt is included so the query refires when the JWT arrives after the
  // initial mount. Without it, anon users fire the query during the JWT-pending
  // window, hit 401, and the cached error never invalidates because userId
  // doesn't change. See Sentry DRAMA-WEB-3M / NocoDB #3957.
  user: (userId: string | null, hasJwt: boolean = false) =>
    [...tierKeys.all, 'user', userId, hasJwt] as const,
  device: (deviceId: string) => [...tierKeys.all, 'device', deviceId] as const,
};

/**
 * useTier - Unified hook for tier resolution
 *
 * Replaces useSubscriptionTier (deprecated) and TierContext (deprecated).
 * Calls backend get_user_tier RPC for authoritative tier resolution.
 */
export function useTier(): UseTierReturn {
  const { user, accessToken, isRealUser } = useAuth();
  const params = useGlobalSearchParams();

  // Development/Staging override: Allow tier testing via URL parameter
  // Usage: http://localhost:8081/?tier=layer2 or https://st.dramadetector.com/?tier=layer2
  const allowTierOverride =
    __DEV__ || process.env.EXPO_PUBLIC_ALLOW_TIER_OVERRIDE === 'true';

  if (allowTierOverride) {
    const tierOverride = params.tier as VisualLayer;
    if (tierOverride && ['layer1', 'layer2', 'layer3'].includes(tierOverride)) {
      // Map visual layer to canonical tier for override
      const overrideTier: TierId =
        tierOverride === 'layer3'
          ? 'dramax'
          : tierOverride === 'layer2'
            ? 'drama_plus'
            : 'free';
      const tierLevel = getTierLevel(overrideTier);
      const dailyLimit =
        overrideTier === 'dramax' ? 50 : overrideTier === 'drama_plus' ? 20 : 3;
      const overrideData: Tier = {
        tier: overrideTier,
        visualLayer: tierOverride,
        source: 'individual',
        familyPlanId: null,
        isFamilyAdmin: false,
        dailyLimit,
        updatedAt: new Date().toISOString(),
      };

      return {
        data: overrideData,
        tier: overrideTier,
        tierLevel,
        visualLayer: tierOverride,
        isLayer1: tierOverride === 'layer1',
        isLayer2: tierOverride === 'layer2',
        isLayer3: tierOverride === 'layer3',
        isPremium: tierOverride === 'layer2' || tierOverride === 'layer3',
        source: 'individual',
        familyPlanId: null,
        isFamilyAdmin: false,
        dailyLimit,
        isLoading: false,
        error: null,
        refetch: () => {},
      };
    }
  }

  // Phase 2 (NocoDB #3869 close-out): use `isRealUser` from AuthContext
  // instead of re-deriving locally. The error fallback below gives `free`
  // tier only when the user is genuinely authed — anon BA users fall through
  // to the default 'anonymous' tier behaviour.
  const isAuthenticated = isRealUser;
  const userId = user?.id || null;

  // Fetch tier via data-proxy for authenticated users, RPC for anonymous
  const {
    data: tierData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: tierKeys.user(userId, !!accessToken),
    queryFn: async (): Promise<Tier> => {
      try {
        // Validated against the shared DTO contract — no field fallbacks.
        // Schema drift surfaces as an immediate runtime error (not silent defaults).
        // Phase 2 (NocoDB #3844 follow-up): drop `skipAuth: true` for anon
        // callers. skipAuth also suppresses X-Device-Id, which after Phase 1's
        // IP-fallback removal is the ONLY identity an anon request can carry
        // before the JWT arrives. Letting authenticatedFetch's default path
        // run attaches Bearer (if present) or X-Device-Id (if anonymous).
        const dto = await fetchJsonValidated(
          TierDto,
          getEdgeFunctionUrl('get-tier'),
        );

        if (__DEV__) {
          console.log('[useTier] Fetched:', { tier: dto.tier, source: dto.source });
        }

        return dto;
      } catch (err) {
        logNetworkError(err as Error, {
          hook: 'useTier',
          endpoint: 'get-tier',
          isAuthenticated,
        });
        if (isAuthenticated) {
          console.warn('[useTier] Fetch failed, defaulting to free:', err);
          return makeIndividualTierData('free');
        }
        // DRAMA-WEB-4E fix: don't re-throw auth errors for anonymous callers.
        // A 401 when unauthenticated is expected (no valid session), not a bug.
        if (isAuthRequiredError(err)) {
          return makeIndividualTierData('free');
        }
        throw err;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes (tier changes are infrequent)
    gcTime: 1000 * 60 * 30, // 30 minutes
    // Gate on JWT availability so we don't fire a pre-Turnstile request that
    // the worker must reject with 401. Both authed and anon users have an
    // accessToken once their BA session is bootstrapped; anon waits for
    // Turnstile → anon sign-in → JWT. Previously `enabled: true` +
    // queryKey(hasJwt) would make React Query refetch on JWT arrival, but the
    // first fire was always a 401 that (a) polluted the network tab, (b) hit
    // logNetworkError's anon-401 filter and bypassed Sentry, and (c) showed up
    // as repeated error clusters on mount. See DRAMA-WEB-3M + NocoDB #3968.
    enabled: !!accessToken,
  });

  // Realtime subscriptions removed (2026-04-02): BA users have no Supabase
  // session for RLS, so Realtime channels were silently failing. Tier changes
  // are infrequent and are refetched on mount.
  // TODO (#2511): Replace with polling or CF Worker WebSocket if needed.

  // Use fetched data or defaults
  const currentTier: Tier = tierData ?? DEFAULT_TIER_DATA;
  const tierLevel = getTierLevel(currentTier.tier);

  return {
    data: currentTier,
    tier: currentTier.tier,
    tierLevel,
    visualLayer: currentTier.visualLayer,
    isLayer1: currentTier.visualLayer === 'layer1',
    isLayer2: currentTier.visualLayer === 'layer2',
    isLayer3: currentTier.visualLayer === 'layer3',
    isPremium:
      currentTier.tier === 'drama_plus' || currentTier.tier === 'dramax',
    source: currentTier.source,
    familyPlanId: currentTier.familyPlanId,
    isFamilyAdmin: currentTier.isFamilyAdmin,
    dailyLimit: currentTier.dailyLimit,
    isLoading,
    error: error as Error | null,
    refetch: () => refetch(),
  };
}

/**
 * Helper hook for character limit based on tier
 * Convenience wrapper around useTier for common use case
 */
export function useCharacterLimit(): number {
  const { tier } = useTier();

  switch (tier) {
    case 'dramax':
      return 5000;
    case 'drama_plus':
      return 2000;
    case 'free':
      return 1000;
    case 'anonymous':
    default:
      return 500;
  }
}
